import { defaultAllSelection } from "services/constants/selections";
import React, { useState, useEffect, useRef } from "react";
import * as Mui from "@mui/material";
import { checkEditorPermission } from "services/utils/HelperFunction";
import {
  themes,
  scrollTop,
  optionalAvathar,
  adminRoleName,
  supportLabel,
} from "services/constants";
import { useSelector } from "react-redux";
import { getContrastingTextColor } from "services/utils/colorChecker";
import { getColorWorkingHours } from "services/utils/Status";
import { tableRowSx } from "services/constants";
import { showToast } from "services/utils/Status";
import { EditCheckInOut } from "pages/TeamZone/Attendance/AttendanceStatus/Status/EditCheckInOut";
import { stickyHeader } from "services/constants";
import { tooltipStyleProps } from "services/constants/Styles";

const attendanceSummaryHeader = [
  { name: "P", key: "Present" },
  { name: "L", key: "Leave" },
  { name: "A", key: "Absent" },
  { name: "PR", key: "Permission" },
];

const LegendItem = ({ label, color, border }) => (
  <Mui.Stack direction="row" alignItems="center" spacing={1}>
    <div
      style={{
        width: 25,
        height: 20,
        backgroundColor: color,
        border: border || "none",
        cursor: "pointer",
      }}
    />
    <Mui.Typography>{label}</Mui.Typography>
  </Mui.Stack>
);

export const PayrunPeriodDateRange = ({
  leavePage,
  setLeavePage,
  attendanceDetails,
  attendanceDataDetails,
  reCallPayScheduleApi,
  viewPayrunDetails,
  masterData,
  colorCode, 
}) => {
  const { userDetails: currentUserDetails, userScreenAccess } = useSelector(
    (state) => state.tokenReducer,
  );

  // Was a ternary keyed on payrollRunCurrentScreen redux state that always
  // resolved to "History" either way (both branches were identical) — and
  // that state is never actually dispatched anywhere in the app, so the
  // condition itself could never even become true. This screen lives under
  // PayrollCycleRun ("running" a payroll cycle), so it checks the "Run"
  // submodule instead — the best available answer given no code anywhere
  // confirms this component is reused across Run Payroll's other three
  // stages (Schedule/Approve/History).
  let check_access = checkEditorPermission(
    userScreenAccess?.module_details,
    "Run Payroll",
    "Run",
  );

  const [departmentId, setDepartmentId] = useState(defaultAllSelection);
  const [leaverowsPerPage, setLeaveRowsPerPage] = useState(25);
  const [shiftDetails, setShiftDetails] = useState({});
  const [openSidePeek, setOpenSidePeek] = useState(false);
  const [editState, setEditState] = useState(false);
  const containerRef = useRef(null);

  const statusLabelMap = React.useMemo(() => {
    if (!Array.isArray(colorCode)) return {};
    return colorCode.reduce((acc, item) => {
      acc[item.status] = item.label;
      return acc;
    }, {});
  }, [colorCode]);

  useEffect(() => {
    const recallApi = () => {
      setEditState(false);
      reCallPayScheduleApi(true);
    };
    if (editState && !openSidePeek) {
      recallApi();
    }
  });

  const handleChangeLeavePage = (event, newPage) => {
    setLeavePage(newPage);
  };

  const handleChangeLeaveRowsPerPage = (event) => {
    setLeaveRowsPerPage(parseInt(event.target.value, 10));
    setLeavePage(0);
  };

  useEffect(() => {
    scrollTop(containerRef);
  }, [leavePage]);

  return (
    <>
      <Mui.Card
        sx={{
          mb: 3,
          boxShadow: "none",
          border: "1px solid #e0e0e0",
          overflow: "visible",
          width: "100%",
        }}
      />

      <Mui.Card style={{ width: "100%" }}>
        <Mui.CardContent style={{ width: "100%" }}>
          <Mui.Grid container>
            <Mui.TableContainer ref={containerRef} sx={{ maxHeight: 510 }}>
              <Mui.Table>
                <Mui.TableHead sx={stickyHeader}>
                  <Mui.TableRow
                    sx={{
                      "& .MuiTableCell-root": {
                        height: "15px",
                        padding: "1px 1px",
                        width: "-1px",
                      },
                    }}
                  >
                    <Mui.TableCell
                      sx={{
                        color: themes.headLine,
                        fontWeight: "bold",
                        fontSize: 15,
                      }}
                    >
                      Name
                    </Mui.TableCell>
                    {attendanceDetails?.headers?.map((header, idx) => (
                      <Mui.TableCell
                        key={idx}
                        sx={{
                          color: themes.headLine,
                          fontWeight: "bold",
                          padding: "4px 2px",
                          minWidth: 38,
                          width: 38,
                        }}
                      >
                        <Mui.Stack>
                          <Mui.Typography textAlign="center" sx={{ fontSize: 10 }}>
                            {header.day_number}
                          </Mui.Typography>
                          <Mui.Typography textAlign="center" sx={{ fontSize: 10 }}>
                            {header.day}
                          </Mui.Typography>
                        </Mui.Stack>
                      </Mui.TableCell>
                    ))}
                    {attendanceSummaryHeader?.map((header, idx) => (
                      <Mui.TableCell
                        key={idx}
                        sx={{ color: themes.headLine, fontWeight: "bold" }}
                      >
                        <Mui.Stack>
                          <Mui.Typography textAlign="center" sx={{ fontSize: 16 }}>
                            {header.name}
                          </Mui.Typography>
                        </Mui.Stack>
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {attendanceDataDetails?.length === 0 ? (
                    <Mui.TableRow sx={tableRowSx}>
                      <Mui.TableCell
                        colSpan={attendanceDetails?.headers?.length + 6}
                        align="center"
                        sx={{ padding: 2 }}
                      >
                        <Mui.Alert severity="info">
                          {masterData?.length === 0
                            ? "No data available."
                            : "No matching data found for your search."}
                        </Mui.Alert>
                      </Mui.TableCell>
                    </Mui.TableRow>
                  ) : (
                    attendanceDataDetails
                      ?.slice(
                        leavePage * leaverowsPerPage,
                        leavePage * leaverowsPerPage + leaverowsPerPage,
                      )
                      ?.map((attendancedata) => (
                        <Mui.TableRow
                          key={attendancedata.user_id}
                          sx={{
                            "& .MuiTableCell-root": {
                              height: "15px",
                              padding: "8px 1px",
                              width: "-1px",
                            },
                          }}
                        >
                          <Mui.TableCell className="column-cell" align="center">
                            <Mui.Stack direction="Row">
                              <Mui.Avatar
                                sx={{ width: 23, height: 23 }}
                                alt={attendancedata?.user?.charAt(0).toUpperCase()}
                                src={attendancedata?.image_url ?? optionalAvathar}
                              />
                              &nbsp;
                              {attendancedata?.user}
                            </Mui.Stack>
                          </Mui.TableCell>
                          {attendancedata?.attendance?.map((dayDetails, idx) => (
                            <Mui.TableCell
                              key={idx}
                              align="center"
                              sx={{ padding: "4px 2px", minWidth: 38, width: 38 }}
                              onClick={() => {
                                // payrollRunCurrentScreen !== "Approval History"
                                // used to be a second condition here — always
                                // true in practice (see check_access above),
                                // so removing it changes nothing.
                                if (check_access) {
                                  if (
                                    dayDetails.legend === "W" ||
                                    (viewPayrunDetails?.status !== "Approved" &&
                                      attendancedata.shift_name !== "Unassigned" &&
                                      dayDetails?.shift_name !== null &&
                                      attendancedata?.user_id !==
                                        currentUserDetails?.id) ||
                                    [adminRoleName, supportLabel].includes(
                                      currentUserDetails?.type,
                                    )
                                  ) {
                                    setDepartmentId(attendancedata?.department_id);
                                    setShiftDetails({
                                      user_id: attendancedata?.user_id,
                                      shift_filter_date: dayDetails?.date,
                                      date: dayDetails?.date
                                        .split("-")
                                        .reverse()
                                        .join("-"),
                                      user_name: attendancedata?.user,
                                      shift_name: dayDetails?.shift_name,
                                      latest_pay_date: attendancedata?.latest_pay_date,
                                      type: dayDetails?.status,
                                      image_url: attendancedata?.image_url,
                                      employee_id: attendancedata?.employee_id,
                                      department_name: attendancedata?.department_name,
                                      user_type: attendancedata?.user_type,
                                    });
                                    setOpenSidePeek(true);
                                  } else {
                                    if (
                                      attendancedata?.user_id === currentUserDetails?.id
                                    ) {
                                      showToast(
                                        "error",
                                        "Your data cannot be edited by yourself",
                                      );
                                    } else if (
                                      dayDetails?.shift_name === "Unassigned" ||
                                      dayDetails?.shift_name === null
                                    ) {
                                      showToast(
                                        "error",
                                        "Unassigned shifts cannot be edited",
                                      );
                                    }
                                  }
                                } else {
                                  showToast(
                                    "error",
                                    "Access denied: You are not authorized to edit the attendance",
                                  );
                                }
                              }}
                            >
                              <Mui.Tooltip
                                arrow
                                slotProps={tooltipStyleProps}
                                placement="top"
                                title={
                                  dayDetails?.legend
                                    ? statusLabelMap[dayDetails.legend]
                                      ? `${dayDetails.legend} — ${statusLabelMap[dayDetails.legend]}`
                                      : dayDetails.legend
                                    : ""
                                }
                              >
                                <div
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "30px",
                                    height: "25px",
                                    backgroundColor: dayDetails.colour_code,
                                    cursor: "pointer",
                                    borderRadius: 3,
                                    flexShrink: 0,
                                  }}
                                >
                                  <Mui.Typography
                                    sx={{
                                      fontSize:
                                        dayDetails?.legend?.length > 2
                                          ? 9
                                          : dayDetails?.legend?.length === 2
                                          ? 11
                                          : 13,
                                      fontWeight: 700,
                                      lineHeight: 1,
                                      whiteSpace: "nowrap",
                                      letterSpacing:
                                        dayDetails?.legend?.length > 2
                                          ? "-0.5px"
                                          : "normal",
                                      color: getContrastingTextColor(
                                        dayDetails?.colour_code
                                          ? dayDetails?.colour_code
                                          : null,
                                      ),
                                    }}
                                  >
                                    {dayDetails.legend}
                                  </Mui.Typography>
                                </div>
                              </Mui.Tooltip>
                            </Mui.TableCell>
                          ))}

                          {/* Summary counts */}
                          {attendanceSummaryHeader?.map((header, idx) => (
                            <Mui.TableCell
                              key={idx}
                              sx={{ color: themes.headLine, fontWeight: "bold" }}
                            >
                              <Mui.Stack>
                                <Mui.Typography textAlign="center" sx={{ fontSize: 10 }}>
                                  {attendancedata?.status_counts[header.key] ||
                                  attendancedata?.status_counts[header.key] === 0
                                    ? attendancedata?.status_counts[header.key]
                                    : "N/A"}
                                </Mui.Typography>
                              </Mui.Stack>
                            </Mui.TableCell>
                          ))}
                        </Mui.TableRow>
                      ))
                  )}
                </Mui.TableBody>
              </Mui.Table>
            </Mui.TableContainer>

            {attendanceDataDetails?.length > 25 ? (
              <Mui.TablePagination
                className="custom-pagination"
                rowsPerPageOptions={[25, 50, 75, 100]}
                component="div"
                count={attendanceDataDetails?.length}
                rowsPerPage={leaverowsPerPage}
                page={leavePage}
                onPageChange={handleChangeLeavePage}
                onRowsPerPageChange={handleChangeLeaveRowsPerPage}
              />
            ) : null}
          </Mui.Grid>

          {openSidePeek ? (
            <EditCheckInOut
              setOpenSidePeek={setOpenSidePeek}
              openSidePeek={openSidePeek}
              shiftDetails={shiftDetails}
              setEditState={setEditState}
              departmentId={departmentId}
              payrollEdit={true}
            />
          ) : null}
        </Mui.CardContent>
      </Mui.Card>
    </>
  );
};